import { CashContractUpgradePanel } from './CashContractUpgradePanel';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { DailyCashKind, VenueInitialCash } from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore, sessionVenueKey } from '../../store/auth.store';
const originalKinds: DailyCashKind[] = ['ZIPA_DRAWER', 'RESERVE', 'PETTY'];
export default function VenueInitialCashPanel({ venueId }: { venueId: string }) {
  const { t } = useTranslation('accounting');
  const revision = useAuthStore((s) => s.sessionRevision),
    actor = useAuthStore((s) => s.user?.id);
  const qc = useQueryClient();
  const [version, setVersion] = useState<1 | 2>(1);
  const kinds: DailyCashKind[] =
    version === 2 ? [...originalKinds, 'OPERATING_REVENUE'] : originalKinds;
  const [values, setValues] = useState<Record<string, string>>({});
  const key = ['venue-initial-cash', venueId, actor, revision];
  const query = useQuery({
    queryKey: key,
    retry: false,
    queryFn: () =>
      api.get<{ configuration: VenueInitialCash | null; locked: boolean }>(
        `/venues/${venueId}/opening-cash`,
      ),
  });
  useEffect(() => {
    setValues({});
    setVersion(query.data?.configuration?.cashBagContractVersion ?? 1);
  }, [
    venueId,
    actor,
    revision,
    query.data?.configuration?.revision,
    query.data?.configuration?.cashBagContractVersion,
  ]);
  const mutation = useMutation({
    mutationFn: () =>
      api.patch(`/venues/${venueId}/opening-cash`, {
        amounts: values,
        cashBagContractVersion: version,
        expectedRevision: query.data?.configuration?.revision ?? null,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: key });
      await qc.invalidateQueries({ queryKey: ['counted-opening-context'] });
    },
  });
  const valid =
    kinds.every((kind) => /^\d{1,10}(\.\d{1,2})?$/.test(values[kind] ?? '')) &&
    (version === 1 || /^0(?:\.0{1,2})?$/.test(values.OPERATING_REVENUE ?? ''));
  return (
    <section
      aria-label={t('counted.configTitle')}
      className="rounded-lg border border-base-300 p-3 space-y-3"
    >
      <h4 className="font-semibold">{t('counted.configTitle')}</h4>
      <p className="text-sm">{t('counted.configHelp')}</p>
      <p className="text-sm">{t('upgrade.threeHelp')}</p>
      {query.isPending && <p role="status">{t('cashSummaryLoading')}</p>}
      {query.isError && <p role="alert">{t('counted.configDenied')}</p>}
      {query.data && (
        <>
          {query.data.locked && <p role="status">{t('counted.configLocked')}</p>}
          {!query.data.locked && (
            <label>
              {t('upgrade.contract')}
              <select
                value={version}
                onChange={(e) => {
                  setVersion(Number(e.target.value) as 1 | 2);
                  setValues({});
                }}
              >
                <option value={1}>{t('upgrade.three')}</option>
                <option value={2}>{t('upgrade.four')}</option>
              </select>
            </label>
          )}
          {query.data.locked &&
            venueId === sessionVenueKey(useAuthStore.getState().accessToken) && (
              <CashContractUpgradePanel key={`${venueId}:${actor}:${revision}`} />
            )}
          {kinds.map((kind) => (
            <label key={kind} className="block">
              {t(`cashWorkflow.bags.${kind}`)}{' '}
              <span className="text-sm">
                {t('counted.currentSetting', {
                  amount: query.data.configuration?.amounts[kind] ?? t('counted.unavailable'),
                })}
              </span>
              {!query.data.locked && (
                <input
                  aria-label={t(`counted.configInput.${kind}`)}
                  className="input input-bordered w-full"
                  inputMode="decimal"
                  autoComplete="off"
                  value={values[kind] ?? ''}
                  disabled={mutation.isPending || query.isFetching}
                  onChange={(e) => setValues((old) => ({ ...old, [kind]: e.target.value }))}
                />
              )}
            </label>
          ))}
          {!query.data.locked && (
            <button
              className="btn min-h-11"
              disabled={!valid || mutation.isPending || query.isFetching}
              onClick={() => mutation.mutate()}
            >
              {t('counted.configSave')}
            </button>
          )}
        </>
      )}
      {mutation.isError && <p role="alert">{t('counted.configFailed')}</p>}
      {mutation.isSuccess && <p role="status">{t('counted.configSaved')}</p>}
      <button
        className="btn btn-ghost min-h-11"
        disabled={mutation.isPending || query.isFetching}
        onClick={() => {
          mutation.reset();
          void query.refetch();
        }}
      >
        {t('cashWorkflow.refresh')}
      </button>
    </section>
  );
}
